import type { AccountSettingsTabId } from "@/constants/accountSettingsSidebar";
import { SERVICE_UNAVAILABLE_PATH } from "./serviceUnavailableRedirect";

/**
 * Routes that stay reachable without Tier 1 (account management + Stripe return URLs).
 * Everything else is redirected to `/paywall` by `BillingPaywallEnforcer`, and gated API
 * 402s also send the user there via `shouldRedirect402ToPaywall`.
 */
/** @deprecated Use dashboard paywall modal; kept if any code still imports the string */
export const BILLING_PAYWALL_PATH = "/paywall";

/**
 * Account settings tabs reachable without Tier 1 — account management + checkout only.
 * Broker management, notifications, connect wizard, etc. stay behind the paywall.
 */
export const PROFILE_TABS_ALLOWED_WITHOUT_TIER1 = [
  "general",
  "authentication",
  "billing",
] as const satisfies ReadonlyArray<AccountSettingsTabId>;

export type ProfileTabAllowedWithoutTier1 =
  (typeof PROFILE_TABS_ALLOWED_WITHOUT_TIER1)[number];

export function isProfileTabAllowedWithoutTier1(
  tabId: string | undefined
): tabId is ProfileTabAllowedWithoutTier1 {
  return (
    !!tabId &&
    (PROFILE_TABS_ALLOWED_WITHOUT_TIER1 as readonly string[]).includes(tabId)
  );
}

/**
 * Returns true if the pathname is allowed when the user does not have Tier 1 access.
 * Only `/profile`, the three allowlisted settings tabs, Stripe return URLs, and `/paywall`.
 */
export function isPathAllowedWithoutTier1(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (path === "/paywall" || path === SERVICE_UNAVAILABLE_PATH) {
    return true;
  }
  // Demo Entry Analysis reveal + generating (claim/TES) for unpaid users
  if (path === "/demo/entry-analysis" || path.startsWith("/demo/entry-analysis/")) {
    return true;
  }
  if (path.startsWith("/billing/")) {
    return true;
  }
  if (path === "/profile") {
    return true;
  }
  if (path.startsWith("/profile/")) {
    const segment = path.replace(/^\/profile\/?/, "").split("/").filter(Boolean)[0];
    // Only exact tab roots: /profile/general, /profile/authentication, /profile/billing.
    // Nested paths like /profile/broker-api/connect stay gated.
    return (
      isProfileTabAllowedWithoutTier1(segment) &&
      path === `/profile/${segment}`
    );
  }
  return false;
}

/**
 * Whether a 402 from this API endpoint should navigate to the paywall.
 * Skips billing endpoints and routes where unsubscribed users are allowed.
 */
export function shouldRedirect402ToPaywall(
  endpoint: string,
  pathname: string = typeof window !== "undefined" ? window.location.pathname : ""
): boolean {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (isPathAllowedWithoutTier1(path)) {
    return false;
  }

  const normalizedEndpoint = endpoint.split("?")[0];
  if (normalizedEndpoint.includes("/billing")) {
    return false;
  }
  // Demo claim / retry-tes must not bounce unpaid users to the app paywall
  if (normalizedEndpoint.includes("/demo/entry-analysis")) {
    return false;
  }

  return true;
}

/** Stripe return URLs: render without sidebar / admin shell — fullscreen modal or redirect only */
export function isBillingStripeReturnFullscreenPath(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, "") || "/";
  return path === "/billing/success" || path === "/billing/canceled";
}
